import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Above modal shells (z-index 71). */
export const FLOATING_PANEL_Z = 150;

/**
 * Position a panel with `position: fixed` via portal so it is not clipped by
 * overflow:hidden / overflow:auto ancestors (modals, tables, cards).
 */
export function FloatingPanel({
  anchorRef,
  open,
  children,
  gap = 6,
  zIndex = FLOATING_PANEL_Z,
  minWidth,
  width,
  align = "start",
  preferAbove = false,
  panelRef: panelRefProp,
}) {
  const internalRef = useRef(null);
  const panelRef = panelRefProp ?? internalRef;
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return undefined;
    }

    let frame = 0;

    const update = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;

      const a = anchor.getBoundingClientRect();
      const p = panel.getBoundingClientRect();
      const panelW = width ?? Math.max(minWidth ?? 0, a.width);
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      let top;
      if (preferAbove) {
        top = a.top - gap - p.height;
        if (top < 8 && a.bottom + gap + p.height <= vh - 8) {
          top = a.bottom + gap;
        }
      } else {
        top = a.bottom + gap;
        if (top + p.height > vh - 8 && a.top - gap - p.height >= 8) {
          top = a.top - gap - p.height;
        }
      }
      if (top < 8) top = 8;

      let left = align === "end" ? a.right - panelW : a.left;
      if (left + panelW > vw - 8) left = vw - panelW - 8;
      if (left < 8) left = 8;

      setRect({ top, left, width: panelW });
    };

    update();
    frame = requestAnimationFrame(update);

    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef, panelRef, gap, minWidth, width, align, preferAbove, children]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top: rect?.top ?? -9999,
        left: rect?.left ?? 0,
        width: rect?.width ?? width ?? minWidth,
        zIndex,
        visibility: rect ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Close when mousedown occurs outside all provided refs. */
export function useDismissOnOutsideClick(open, onDismiss, refs) {
  const refList = refs.filter(Boolean);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (refList.some((ref) => ref.current?.contains(e.target))) return;
      onDismiss();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onDismiss, refList]);
}
